import React, { useEffect, useState } from 'react';
import type { SkillName, SkillLevelUpEvent } from '@shared/types/skills';
import './SkillLevelUpNotification.css';

interface NotificationItem extends SkillLevelUpEvent {
  id: number;
}

interface SkillLevelUpNotificationProps {
  onLevelUp?: (event: SkillLevelUpEvent) => void;
}

/**
 * Component that displays a toast notification when player levels up a skill.
 * Listens to socket 'skill:levelUp' events from the SkillManager.
 */
export const SkillLevelUpNotification: React.FC<SkillLevelUpNotificationProps> = ({ onLevelUp }) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [nextId, setNextId] = useState(0);

  useEffect(() => {
    // This hook would be called from GameCanvas with the socket instance
    // We'll handle it through a custom hook or context
  }, []);

  const addNotification = (event: SkillLevelUpEvent) => {
    const id = nextId;
    setNextId(prev => prev + 1);
    
    const newNotification: NotificationItem = { ...event, id };
    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove after 4 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);

    // Callback if provided
    onLevelUp?.(event);
  };

  // Expose the method for external use
  (window as any).__addSkillLevelUpNotification = addNotification;

  const getSkillIcon = (skill: SkillName): string => {
    const skillIcons: Partial<Record<SkillName, string>> = {
      // Combat
      gunnery: '[G]',
      tactics: '[T]',
      // Gathering
      fishing: '[F]',
      salvaging: '[S]',
      foraging: '[R]',
      mining: '[M]',
      // Production
      shipwright: '[W]',
      cooking: '[C]',
      smithing: '[I]',
      alchemy: '[A]',
      // Utility
      trading: '[P]',
    };
    return skillIcons[skill] || '[+]';
  };

  const getSkillDisplayName = (skill: SkillName): string => {
    return skill.charAt(0).toUpperCase() + skill.slice(1);
  };

  return (
    <div className="skill-level-up-notifications">
      {notifications.map(notification => (
        <div
          key={notification.id}
          className="skill-level-up-notification animate-in"
        >
          <div className="notification-content">
            <span className="skill-emoji">
              {getSkillIcon(notification.skill)}
            </span>
            <div className="notification-text">
              <div className="skill-name">
                {getSkillDisplayName(notification.skill)} Level Up!
              </div>
              <div className="level-change">
                Level {notification.oldLevel} → {notification.newLevel}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
